// Angular
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  Compiler,
  COMPILER_OPTIONS,
  CompilerFactory,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, Title } from '@angular/platform-browser';
import { JitCompilerFactory } from '@angular/platform-browser-dynamic';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
// Packages
import { AppStateModule } from '@lbmx/app-state';
import { AuthModule } from '@lbmx/phoenix-lib-auth';
import { CoreModule } from '@lbmx/phoenix-lib-core';
import { UtilsModule } from '@lbmx/phoenix-lib-utils';
import { SearchContainerModule } from '@lbmx/search-container';
// Transloco
import {
  TRANSLOCO_CONFIG,
  TranslocoConfig,
  TranslocoModule,
} from '@ngneat/transloco';
import { TranslocoMessageFormatModule } from '@ngneat/transloco-messageformat';
// Kendo
import {
  CldrIntlService,
  IntlModule,
  IntlService,
} from '@progress/kendo-angular-intl';
import '@progress/kendo-angular-intl/locales/en-AU/all';
import '@progress/kendo-angular-intl/locales/en-AU/calendar';
import '@progress/kendo-angular-intl/locales/en-CA/all';
import '@progress/kendo-angular-intl/locales/en-gb/all';
import '@progress/kendo-angular-intl/locales/en-gb/calendar';
import '@progress/kendo-angular-intl/locales/en-NZ/all';
import '@progress/kendo-angular-intl/locales/en/all';
import '@progress/kendo-angular-intl/locales/en/calendar';
import '@progress/kendo-angular-intl/locales/es/all';
import '@progress/kendo-angular-intl/locales/fr/all';
import '@progress/kendo-angular-intl/locales/ru/all';

// UI Modules - Prime
import { EditorModule } from 'primeng';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MegaMenuModule } from 'primeng/megamenu';
import { MenubarModule } from 'primeng/menubar';
import { PasswordModule } from 'primeng/password';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { TreeTableModule } from 'primeng/treetable';
import { httpLoader } from 'src/loaders/http.loader';
import { BreadcrumbModule, BreadcrumbService } from 'xng-breadcrumb';

// Public Component
import { LoginComponent } from './features/public/login/login.component';

// Secure Component
import { HomeComponent } from './features/secure/home/home.component';
import { OtpComponentNew } from './features/secure/otp-no-dialog/otp.component';
import { OtpComponent } from './features/secure/otp/otp.component';

// App
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

// Environments and loaders
import { NewMemberLocationComponent } from './features/public/group/new-member/new-member/new-member-location/new-member-location.component';
import { NewMemberComponent } from './features/public/group/new-member/new-member/new-member.component';
import { NewSupplierComponent } from './features/public/group/new-supplier/new-supplier/new-supplier.component';
import { LogoutComponent } from './features/public/logout/logout.component';
import { RecoverPasswordComponent } from './features/public/recover-password/recover-password.component';
import { RegisterComponent } from './features/public/register/register.component';
import { ResetPasswordComponent } from './features/public/reset-password/reset-password.component';
import { ErrorComponent } from './features/secure/error/error.component';
import { MainFooterComponent } from './features/secure/main-footer/main-footer.component';
import { MainHeaderComponent } from './features/secure/main-header/main-header.component';
import { MainModuleSelectorComponent } from './features/secure/main-navigation-container/main-module-selector/main-module-selector.component';
import { LBMXMegaMenuComponent } from './features/secure/main-navigation-container/main-nav-bar/lbmx-nav-bar/lbmx-mega-menu/lbmx-mega-menu.component';
import { LBMXNavBarComponent } from './features/secure/main-navigation-container/main-nav-bar/lbmx-nav-bar/lbmx-nav-bar.component';
import { MainNavBarComponent } from './features/secure/main-navigation-container/main-nav-bar/main-nav-bar.component';
import { MainNavigationContainerComponent } from './features/secure/main-navigation-container/main-navigation-container.component';
import { ScrollTopComponent } from './features/secure/main-scroll-top/main-scroll-top.component';
import { MemberLocationComponent } from './features/secure/onboarding/member/member-location/member-location.component';
// import { OnboardingModule } from './features/secure/onboarding/onboarding.module';
import { MemberComponent } from './features/secure/onboarding/member/member.component';
import { SupplierComponent } from './features/secure/onboarding/supplier/supplier.component';
import { TermsServicesComponent } from './features/secure/terms-services/terms-services.component';
import { UserProfileContainerComponent } from './features/secure/userprofile/user-profile-container';
import { UserprofileComponent } from './features/secure/userprofile/userprofile.component';
import { PublicComponent } from './layouts/public/public.component';
import { OnMouseMoveDirective } from './layouts/secure/on-mouse-move.directive';
import { SecureComponent } from './layouts/secure/secure.component';
import { ConfigProvider } from './provider/config-provider';

import { ContactSupportComponent } from './features/secure/contact-support/contact-support.component';
import { DocumentSpecificationsComponent } from './features/secure/document-specifications/document-specifications.component';
import { TestToolGuideComponent } from './features/secure/test-tool-guide/test-tool-guide.component';

// Services
import { AuthGuardService } from './services/authGuard/authGuard.service';
import { EnvServiceProvider } from './services/EnvServiceProvider';
import { RequestInterceptor } from './services/error/http.interceptor';

export function createCompiler(compilerFactory: CompilerFactory) {
  return compilerFactory.createCompiler();
}

export function configProviderFactory(provider: ConfigProvider) {
  return () => provider.load();
}

@NgModule({
  imports: [
    AppStateModule,
    AppRoutingModule,
    BrowserModule,
    BreadcrumbModule,
    ButtonModule,
    BrowserAnimationsModule,
    CheckboxModule,
    CardModule,
    CoreModule,
    DropdownModule,
    DialogModule,
    EditorModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    PasswordModule,
    MegaMenuModule,
    MenubarModule,
    UtilsModule,
    AuthModule,
    TranslocoModule,
    TabViewModule,
    IntlModule,
    ToastModule,
    TreeTableModule,
    TranslocoMessageFormatModule.init(),
    TabViewModule,
    ConfirmDialogModule,
    SearchContainerModule,
  ],
  declarations: [
    OnMouseMoveDirective,
    AppComponent,
    UserProfileContainerComponent,
    PublicComponent,
    LoginComponent,
    RecoverPasswordComponent,
    ResetPasswordComponent,
    SecureComponent,
    HomeComponent,
    MainHeaderComponent,
    MainNavigationContainerComponent,
    MainNavBarComponent,
    LBMXNavBarComponent,
    LBMXMegaMenuComponent,
    MainFooterComponent,
    ScrollTopComponent,
    RegisterComponent,
    MainModuleSelectorComponent,
    UserprofileComponent,
    LogoutComponent,
    TermsServicesComponent,
    ErrorComponent,
    MemberComponent,
    SupplierComponent,
    NewMemberComponent,
    NewSupplierComponent,
    MemberLocationComponent,
    NewMemberLocationComponent,
    ContactSupportComponent,
    DocumentSpecificationsComponent,
    ContactSupportComponent,
    TestToolGuideComponent,
    OtpComponent,
    OtpComponentNew
  ],
  entryComponents: [OtpComponent, OtpComponentNew],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    httpLoader,
    {
      provide: TRANSLOCO_CONFIG,
      useValue: {
        listenToLangChange: true,
        reRenderOnLangChange: true,
        defaultLang: 'en-ca',
        fallbackLang: 'en-ca',
        useFallbackTranslation: true,
        scopeStrategy: 'shared',
      } as TranslocoConfig,
    },
    AuthGuardService,
    BreadcrumbService,
    EnvServiceProvider,
    ConfigProvider,
    {
      provide: COMPILER_OPTIONS,
      useValue: {},
      multi: true,
    },
    {
      provide: CompilerFactory,
      useClass: JitCompilerFactory,
      deps: [COMPILER_OPTIONS],
    },
    {
      provide: Compiler,
      useFactory: createCompiler,
      deps: [CompilerFactory],
    },
    CldrIntlService,
    {
      provide: IntlService,
      useExisting: CldrIntlService,
    },
    {
      provide: LOCALE_ID,
      useValue: 'en-ca',
    },
    {
      provide: APP_INITIALIZER,
      useFactory: configProviderFactory,
      deps: [ConfigProvider],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true,
    },
    Title,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
